import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExpandMoreOutlined, ExpandLessOutlined, FactCheckOutlined, HistoryOutlined } from '../../icons';
import { SUBJECT_COLOR_PALETTE } from '../../utils/subjectColors';
import { getProgressGradientColor } from '../../utils/progressColor';
import { useTheme } from '../../contexts/ThemeContext';
import '../../dashboard/MyMocksPage.css';

// Painel "Progresso do edital" (aba Home): cobertura de conteúdo — quantos
// tópicos do edital já foram estudados (via Diário de estudos), matéria por
// matéria, diferente do painel de desempenho (que mede % de acerto). Reaproveita
// a mesma estrutura visual (stat-item/stat-topic-item) do painel de desempenho e
// a mesma paleta categórica por matéria do Plano de Estudos (SUBJECT_COLOR_PALETTE)
// nas barras/tracinhos por matéria. O donut geral é exceção: usa um gradiente
// vermelho->laranja->verde (getProgressGradientColor) porque representa progresso
// (0-100%), não uma categoria.
const EditalProgressPanel = ({ overview, data }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [expandedSubjects, setExpandedSubjects] = useState(() => new Set());

  const goToHistory = (subjectId, subjectName, topicId, topicName) => {
    navigate('/diario-de-estudos', { state: { subjectId, subjectName, topicId, topicName } });
  };

  const toggleSubject = (materia) => {
    setExpandedSubjects((prev) => {
      const next = new Set(prev);
      if (next.has(materia)) {
        next.delete(materia);
      } else {
        next.add(materia);
      }
      return next;
    });
  };

  if (!overview || overview.totalTopicos === 0) {
    return null;
  }

  const donutDeg = Math.min(overview.percentual, 100) * 3.6;
  const donutColor = getProgressGradientColor(overview.percentual, theme);

  return (
    <div className="edital-progress-panel">
      <div className="edital-progress-header">
        <span className="edital-progress-icon">
          <FactCheckOutlined style={{ fontSize: 20 }} />
        </span>
        <div>
          <h3>Progresso do edital</h3>
          <span className="edital-progress-subtitle">
            Acompanhe quanto do conteúdo cobrado na prova você já estudou, matéria por matéria e tópico por tópico.
          </span>
        </div>
      </div>

      <div className="edital-progress-overview">
        <div className="edital-donut-card">
          <div className="edital-donut" style={{ backgroundImage: `conic-gradient(${donutColor} 0deg ${donutDeg}deg, var(--bg-card-alt) ${donutDeg}deg 360deg)` }}>
            <div className="edital-donut-inner">
              <span className="edital-donut-value">{overview.percentual}%</span>
              <span className="edital-donut-label">do edital</span>
            </div>
          </div>
          <span className="edital-donut-caption">
            {overview.topicosEstudados} de {overview.totalTopicos} tópicos estudados
          </span>
        </div>

        <div className="kpi-strip edital-kpi-strip">
          <div className="kpi-card kpi-card--primary">
            <span className="kpi-label">MATÉRIAS INICIADAS</span>
            <span className="kpi-value">{overview.materiasIniciadas} <small className="kpi-value-suffix">de {overview.materiasTotal}</small></span>
          </div>
          <div className="kpi-card">
            <span className="kpi-label">MATÉRIA MAIS AVANÇADA</span>
            <span className="kpi-value kpi-value--small">
              {overview.materiaMaisAvancada ? overview.materiaMaisAvancada.materia : '—'}
            </span>
            {overview.materiaMaisAvancada && (
              <span className="kpi-sub kpi-sub--emph">{overview.materiaMaisAvancada.percentual}% do conteúdo</span>
            )}
          </div>
          <div className="kpi-card kpi-card--exam">
            <span className="kpi-label">PRECISA DE ATENÇÃO</span>
            <span className="kpi-value kpi-value--small">
              {overview.materiaPrecisaAtencao ? overview.materiaPrecisaAtencao.materia : '—'}
            </span>
            {overview.materiaPrecisaAtencao && (
              <span className="kpi-sub kpi-sub--emph">{overview.materiaPrecisaAtencao.percentual}% do conteúdo</span>
            )}
          </div>
        </div>
      </div>

      <div className="edital-subject-list">
        {data.map((item, index) => {
          const subjectColor = SUBJECT_COLOR_PALETTE[index % SUBJECT_COLOR_PALETTE.length].border;
          const isExpanded = expandedSubjects.has(item.materia);
          return (
            <div key={item.materia} className="edital-subject-card">
              <div
                className="edital-subject-row"
                onClick={() => toggleSubject(item.materia)}
                role="button"
                tabIndex={0}
              >
                <span className="edital-subject-stripe" style={{ backgroundColor: subjectColor }} />
                <div className="edital-subject-body">
                  <div className="stat-header">
                    <span>{item.materia}</span>
                    <strong>{item.percentual}%</strong>
                  </div>
                  <div className="stat-bar-track">
                    <div
                      className="stat-bar-fill"
                      style={{ width: `${Math.min(item.percentual, 100)}%`, background: subjectColor }}
                    />
                  </div>
                  <small>{item.estudados} de {item.total} tópicos estudados</small>
                </div>
                <button
                  type="button"
                  className="edital-history-btn"
                  aria-label={`Ver histórico de ${item.materia}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    goToHistory(item.materiaId, item.materia);
                  }}
                >
                  <HistoryOutlined style={{ fontSize: 18 }} />
                </button>
                {isExpanded ? (
                  <ExpandLessOutlined style={{ fontSize: 18, flexShrink: 0 }} />
                ) : (
                  <ExpandMoreOutlined style={{ fontSize: 18, flexShrink: 0 }} />
                )}
              </div>

              {isExpanded && (
                <div className="edital-topic-list">
                  {item.topicos.map((topico) => {
                    const topicPercentage = topico.percentual;
                    return (
                      <div
                        key={topico.topicoId}
                        className="edital-topic-row edital-topic-row--clickable"
                        onClick={() => goToHistory(item.materiaId, item.materia, topico.topicoId, topico.topico)}
                        role="button"
                        tabIndex={0}
                      >
                        <span className="edital-topic-stripe" style={{ backgroundColor: subjectColor }} />
                        <div className="edital-topic-body">
                          <div className="stat-header">
                            <span>{topico.topico}</span>
                            <strong>{topicPercentage}%</strong>
                          </div>
                          <div className="stat-bar-track">
                            <div
                              className="stat-bar-fill"
                              style={{ width: `${topicPercentage}%`, background: subjectColor }}
                            />
                          </div>
                        </div>
                        <HistoryOutlined className="edital-topic-history-icon" style={{ fontSize: 16 }} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EditalProgressPanel;
